/**
 * Algolia InstantSearch Integration for Shopify
 * 
 * This file initializes and configures Algolia's InstantSearch for the Shopify theme,
 * enabling product search, filtering, and merchandising capabilities. It supports
 * both full search results pages and collection-specific search with custom filtering.
 * 
 * Key features:
 * - InstantSearch widget initialization for products
 * - Collection page filtering with configurable facet rules
 * - Stock policy filtering (allow/deny/continue)
 * - Allowed collections filtering for multi-store support
 * - URL routing with foreign parameter preservation (UTM params)
 * - Custom price display via metafields
 * - Analytics integration for conversion tracking
 * 
 * @module algolia_instant_search
 * @version 2.0.0
 * @requires Algolia InstantSearch
 * @requires Algolia Search API
 * @requires algolia_custom_helpers
 * 
 * @example
 * // This file is compiled from Liquid to JS and executes automatically
 * // when the Algolia Shopify integration is loaded on search/collection pages.
 */

/* eslint-disable object-shorthand */
/* eslint-disable prefer-arrow-callback */
/* eslint-disable vars-on-top */
/* eslint-disable strict */
/* eslint-disable no-var */
/* eslint-disable complexity */

/**
 * Main IIFE that initializes Algolia InstantSearch.
 * 
 * This async function sets up the entire InstantSearch instance including:
 * - Configuration retrieval from algolia config object
 * - Index selection (with profit margin index support)
 * - Stock policy filtering
 * - Collection page filtering
 * - Widget registration (search box, hits, facets, pagination, etc.)
 * 
 * @async
 * @function
 * @param {Object} algolia - Global Algolia configuration and helpers object
 * @param {Object} algolia.config - Configuration object containing app_id, search_api_key, etc.
 * @param {Object} algolia.externals - External InstantSearch widgets and connectors
 * @param {boolean} algolia.full_results - Whether we're on a full search results page
 * @param {boolean} algolia.is_collection_results_page - Whether we're on a collection page
 * @returns {void} Early returns if Algolia is not available or instant search is disabled
 */
(async function(algolia) {
  'use strict';
  var instantsearch = window.instantsearch;
  var algoliasearch = window.algoliasearch.algoliasearch;
  if(typeof algoliasearch == "undefined") return;
  var searchBox = algolia.externals.widgets.searchBox;
  var stats = algolia.externals.widgets.stats;
  var sortBy = algolia.externals.widgets.sortBy;
  var clearRefinements = algolia.externals.widgets.clearRefinements;
  var panel = algolia.externals.widgets.panel;
  var hits = algolia.externals.widgets.hits;
  var pagination = algolia.externals.widgets.pagination;
  var configure = algolia.externals.widgets.configure;
  var index_suffix = await algolia.config.index_suffix;
  var connectCurrentRefinements =
    algolia.externals.connectors.connectCurrentRefinements;

  let algolia_index_name = algolia.config.index_prefix + 'products' + index_suffix;

  /**
   * Determines which Algolia index to use for searching.
   * 
   * If the collection_sort_by_featured flag is set and a profit margin index exists,
   * we use that index for featured product sorting on collections.
   * 
   * @example
   * // Default: uses products_index_suffix (e.g., "products_main")
   * // When featured: uses algolia_profit_margin_index for sorted results
   */
  if(window.collection_sort_by_featured && window.algolia_profit_margin_index.length > 0) {
    algolia_index_name = window.algolia_profit_margin_index;
  }

  var collectionPageEnabled =
    algolia.is_collection_results_page &&
    algolia.config.instant_search_enabled_on_collection;

  if (!algolia.full_results && !algolia.is_collection_results_page) {
    /**
     * If we aren't on the search page and neither on a collection page,
     * then there's no need to proceed
     */
    return;
  } else if (algolia.full_results) {
    /**
     * If we are on the search page and instant search isn't enabled,
     * then there's no need to proceed
     */
    if (!algolia.config.instant_search_enabled) {
      return;
    }
  } else if (algolia.is_collection_results_page) {
    /**
     * If we are on a collection page and instant search on collection isn't
     * enabled, then there's no need to proceed
     */
    if (!algolia.config.instant_search_enabled_on_collection) {
      return;
    }
  }

  /**
   * Array which will contain all filters to be applied while initiating the
   * search API call.
   */
  var searchFilters = [];

  var collectionFacetFilter = null;
  var collectionRulesContextValue = null;
  var collectionHandle = null;

  if (collectionPageEnabled) {
    var matches = window.location.pathname.match(/\/collections\/([^/]+)/i);
    collectionHandle =
      Boolean(matches) && matches.length === 2 ? matches[1] : null;

    if (algolia.config.collection_id_indexing) {
      collectionFacetFilter = algolia.current_collection_id
        ? 'collections:"' + algolia.current_collection + '"'
        : null;
    } else {
      collectionFacetFilter = 'collections:"' + collectionHandle + '"';
    }

    // Add the collection filter to the list of search filters
    searchFilters.push(collectionFacetFilter);

    collectionRulesContextValue = algolia.config.collection_id_query_rules
      ? algolia.current_collection_id
      : collectionHandle;
  }

  // Filters for stock policy
  /**
   * Stock policy filter based on Algolia configuration.
   * 
   * Controls which products are displayed based on inventory status:
   * - 'allow': Display all products including out of stock items
   * - 'deny': Only show products with inventory_quantity > 0
   * - 'continue': Show products that are either in stock OR have inventory_policy set to 'continue'
   * 
   * The filter is built using Algolia's numeric filters syntax and added to
   * the searchFilters array to be applied with AND logic.
   * 
   * @see https://www.algolia.com/doc/api-reference/search-api-parameters/#filters
   */
  var stockPolicyFilter = null;
  if (algolia.config.stock_policy) {
    if (algolia.config.stock_policy === 'allow') {
      /**
       * For 'allow', we don't need to add any filter as we want to continue
       * displaying all out of stock items.
       */
    } else if (algolia.config.stock_policy === 'deny') {
      // For 'deny' we will filter out all items based on inventory quantity
      stockPolicyFilter = 'inventory_quantity > 0';
    } else if (algolia.config.stock_policy === 'continue') {
      /**
       * For 'continue' we will filter on `inventory_available` attribute whose
       * value is dependent on:
       * `inventory_quantity > 0 OR inventory_policy == 'continue'`
       */
      stockPolicyFilter = 'inventory_available:true';
    }

    // Add the stock policy filter to the list of search filters
    if (stockPolicyFilter) {
      searchFilters.push(stockPolicyFilter);
    }
  }

  var results_selector = collectionPageEnabled
    ? algolia.config.collection_css_selector
    : algolia.config.results_selector;

  var activeSortOrders =
    collectionPageEnabled && algolia.collectionSortOrders
      ? algolia.collectionSortOrders
      : algolia.sortOrders;

  results_selector += ', .algolia-shopify-instantsearch';

  /**
   * Extracts the UI state that should be tracked in the URL.
   * 
   * Filters out configuration parameters and query parameters that are handled
   * separately, keeping only the actual UI state (refinements, pagination, etc.)
   * to be serialized into the URL for shareable/searchable links.
   * 
   * @function getTrackedUiState
   * @param {Object} uiState - The full UI state from InstantSearch
   * @returns {Object} Filtered UI state containing only tracked parameters
   * @example
   * // Input: { configure: {...}, query: "shoes", refinementList: { brand: ["Nike"] } }
   * // Output: { refinementList: { brand: ["Nike"] } }
   */
  function getTrackedUiState(uiState) {
    var trackedUiState = {};
    Object.keys(uiState).forEach(function(k) {
      if (k === 'configure' || k === 'query' || k === 'q') {
        return;
      }
      trackedUiState[k] = uiState[k];
    });
    return trackedUiState;
  }

  /**
   * List of URL params which are "allowed" by InstantSearch.
   * These parameters won't be overwritten when the search state is written
   * to the URL.
   * 
   * This allows UTM tracking parameters to persist through search interactions,
   * preserving analytics attribution even when users filter or sort results.
   */
  var ALLOWED_FOREIGN_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ];
  var foreignRouteState;

  /**
   * Creates routing configuration for a single Algolia index.
   * 
   * Provides bidirectional mapping between InstantSearch UI state and URL routes:
   * - stateToRoute: Transforms UI state to URL-compatible route object
   * - routeToState: Restores UI state from URL route on page load
   * 
   * Handles preservation of "foreign" parameters (like UTM tags) that should
   * not be overwritten by InstantSearch state changes.
   * 
   * @function singleIndex
   * @param {string} indexName - The Algolia index name to create routing for
   * @returns {Object} Routing configuration with stateToRoute and routeToState methods
   */
  function singleIndex(indexName) {
    return {
      /**
       * `stateToRoute` method transforms the `uiState` object from InstantSearch
       * into an object that will then be serialized to construct the URL.
       */
      stateToRoute: function(uiState) {
        var route = getTrackedUiState(uiState[indexName] || {});
        route.q = uiState[indexName].query;
        return Object.assign({}, foreignRouteState, route);
      },

      /**
       * `routeToState` method transforms the route object into a `uiState` object
       * so that InstantSearch is initialized with the correct state from the
       * URL.
       */
      routeToState: function(routeState) {
        /**
         * Backup parameters from `routeState` into a different object
         * `foreignRouteState` so that we can re-inject them into the object
         * returned within `stateToRoute`.
         * This allows us to preserve the parameters specified in `ALLOWED_FOREIGN_PARAMS`
         * array.
         */
        if (!foreignRouteState) {
          foreignRouteState = ALLOWED_FOREIGN_PARAMS.reduce(function(acc, key) {
            acc[key] = routeState[key];
            return acc;
          }, {});
        }

        var state = {};
        state[indexName] = getTrackedUiState(routeState || {});
        state[indexName].query = routeState.q;
        return state;
      },
    };
  }

  var instant = {
    colors: algolia.config.colors,
    distinct: Boolean(algolia.config.show_products),
    facets: {
      hidden:
        collectionPageEnabled && algolia.collectionHiddenFacets
          ? algolia.collectionHiddenFacets
          : algolia.hiddenFacets,
      shown:
        collectionPageEnabled && algolia.collectionShownFacets
          ? algolia.collectionShownFacets
          : algolia.shownFacets,
      list:
        collectionPageEnabled && algolia.collectionFacets
          ? algolia.collectionFacets
          : algolia.facets,
      widgets:
        collectionPageEnabled && algolia.collectionFacetsWidgets
          ? algolia.collectionFacetsWidgets
          : algolia.facetsWidgets,
    },
    hitsPerPage:
      collectionPageEnabled &&
      algolia.config.collections_full_results_hits_per_page
        ? algolia.config.collections_full_results_hits_per_page
        : algolia.config.products_full_results_hits_per_page,
    search: instantsearch({
      searchClient: algoliasearch(
        algolia.config.app_id,
        algolia.config.search_api_key
      ),
      insights: algolia.config.analytics_enabled,
      indexName: algolia_index_name,
      routing: {
        stateMapping: singleIndex(algolia_index_name),
      },
      searchFunction: function(searchFunctionHelper) {
        // Set query parameters here because they're not kept when someone
        // presses the Back button if set in the `init` function of a custom widget
        var page = searchFunctionHelper.getPage();
        if (instant.distinct) {
          searchFunctionHelper.setQueryParameter('distinct', true);
        }

        /**
         * Collection Filtering Logic
         * 
         * This section handles filtering products based on collection membership,
         * supporting three types of collection filters for multi-store setups:
         * 
         * 1. allowed_collections: Whitelist of collections that can be shown
         *    - Products must belong to at least one of these collections
         *    - Used to restrict search results to store-specific collections
         * 
         * 2. wizard_collections: Special "wizard" collections for filtered browsing
         *    - These are additional collections that can be included in results
         *    - Often used for curated collections or seasonal promotions
         * 
         * 3. has_filter_collection: Additional filter collection (usually current collection)
         *    - When on a specific collection page, this ensures products
         *      are also in that collection
         * 
         * Filter Construction Rules:
         * - If only allowed_collections: Products must be in one of them
         * - If only wizard_collections: Products must be in one of them
         * - If both: Products must be in allowed OR wizard collections
         * - has_filter_collection adds AND requirement for current collection
         * 
         * Example filter outputs:
         * - Basic: (collections:accessories OR collections:womens-tops)
         * - With wizard: (collections:accessories OR collections:sale-items)
         * - With filter: ... AND collections:"womens-tops"
         * - With exclusions: ... AND NOT collections:restricted
         */
        // Handle collection filtering
        if ((window.allowed_collections && window.allowed_collections.length > 0) ||
          (window.wizard_collections && window.wizard_collections.length > 0)) {
          /**
           * Builds the allowed collections filter string.
           * 
           * Maps each allowed collection handle to Algolia's filter syntax:
           * Input: ['accessories', 'womens-tops']
           * Output: '(collections:accessories OR collections:womens-tops'
           * 
           * The trailing parenthesis is added later based on whether
           * wizard_collections are also present.
           */
          let allowedCollectionsFilter = window.allowed_collections ?  '(' + window.allowed_collections.map(c => 'collections:' + c).join(' OR ') : '(';

          /**
           * Builds wizard collections filter string.
           * 
           * Similar to allowed_collections but for wizard-specific collections.
           * These are OR'd with allowed_collections to expand the result set.
           */
          const wizard_collections = window.wizard_collections ? window.wizard_collections.map(c => 'collections:' + c).join(' OR ') : null;

          /**
           * Scenario 1: On a wizard collection page with filter collection
           * 
           * When user is browsing a wizard collection AND has_filter_collection is set,
           * we add both the wizard filter AND the current collection filter.
           * This ensures products match the wizard criteria AND are in current collection.
           */
          if (collectionRulesContextValue &&
            window.has_filter_collection &&
            wizard_collections &&
            wizard_collections.length > 0 &&
            window.wizard_collections.includes(window.current_collection)
          ) {
              if(allowedCollectionsFilter.length > 1 && allowedCollectionsFilter.slice(-3) != 'OR ') {
                allowedCollectionsFilter += ' OR '
              }
              allowedCollectionsFilter += wizard_collections + ') AND collections:"' + window.has_filter_collection + '"';
          }
          /**
           * Scenario 2: On search page (not collection) with filter collection
           * 
           * When on the main search page with a filter collection set,
           * we build a combined filter that includes both allowed/wizard collections
           * AND the filter collection. This allows search results to respect
           * both the collection restrictions and the filter.
           * 
           * Format: ') OR (wizard_collections) AND has_filter_collection'
           */
          else if (!collectionRulesContextValue && window.has_filter_collection && typeof window.current_collection == "undefined") {
            let searchFilters = allowedCollectionsFilter;
            // do the main part - use allowed collections and wizard collections
            if (wizard_collections && wizard_collections.length > 0) {
              if(searchFilters != '(' &&
                searchFilters.slice(-3) != 'OR '
              ) {
                searchFilters += ' OR '
              }
              searchFilters += `${wizard_collections}) AND collections:"${window.has_filter_collection}"`;
            } else searchFilters += ')';
            allowedCollectionsFilter = searchFilters;
          } 
          /**
           * Scenario 3: Standard case - just combine allowed and wizard collections
           * 
           * When not in special filtering scenarios, simply OR the allowed
           * and wizard collections together to broaden results.
           */
          else {
            if (wizard_collections && wizard_collections.length > 0) {

              if(allowedCollectionsFilter.length > 1 &&
                allowedCollectionsFilter.slice(-3) != 'OR ') {
                allowedCollectionsFilter += ' OR '
              }
              allowedCollectionsFilter += wizard_collections + ')';
            } else {
              allowedCollectionsFilter += ')';
            }
          }

          // Avoid duplicate filters by checking if identical filter already exists
          if (!searchFilters.some(f => f == allowedCollectionsFilter)) {
            searchFilters.push(allowedCollectionsFilter);
          }
        }

        /**
         * Tag-based product exclusion filter.
         * 
         * If window.exclude_tag is set, products with that tag are excluded
         * from search results. This is useful for hiding products that should
         * not appear in search (e.g., internal products, draft items).
         */
        if (typeof window.exclude_tag != "undefined" && window.exclude_tag.length > 0) {
          searchFilters.push(' NOT tags:' + window.exclude_tag)
        }

        /**
         * Combines all search filters into a single filter string.
         * 
         * All filters in the searchFilters array are joined with AND logic,
         * meaning all conditions must be met for a product to appear in results.
         */
        let filters = searchFilters.join(' AND ');

        // Assign any required filters
        if (filters.length) {
          searchFunctionHelper.setQueryParameter(
            'filters',
            filters
          );
        }

        // Assign any required `ruleContexts` which are required for query rules
        // targeting collection pages
        if (collectionPageEnabled) {
          // Collection page merchandising:
          // If we are on a collection page, `collectionRulesContextValue` is defined
          if (collectionRulesContextValue) {
            searchFunctionHelper.setQueryParameter('ruleContexts', [
              collectionRulesContextValue.toString(),
            ]);
          } else {
            searchFunctionHelper.setQueryParameter('ruleContexts', []);
          }
        }

        searchFunctionHelper.setPage(page);
        searchFunctionHelper.search();
      },
    }),
    selector: results_selector + ', .algolia-shopify-instantsearch',
    sortOrders: activeSortOrders
  };

  instant.search.client.addAlgoliaAgent('Shopify Integration');

  algolia.instantsearch = instant;

  /**
   * Adjusts CSS classes based on the results container width.
   * 
   * Responsive helper that updates the container's size class based on
   * breakpoints. This allows the UI to adapt its layout for different
   * screen sizes (xs, sm, md, lg).
   * 
   * @function readjust
   * @returns {void}
   */
  function readjust() {
    var width = instant.$results.offsetWidth;

    var suffix = 'lg';
    if (width < 400) suffix = 'xs';
    else if (width < 800) suffix = 'sm';
    else if (width < 1200) suffix = 'md';

    instant.$results.classList.remove('ais-results-size-xs');
    instant.$results.classList.remove('ais-results-size-sm');
    instant.$results.classList.remove('ais-results-size-md');
    instant.$results.classList.remove('ais-results-size-lg');
    instant.$results.classList.add('ais-results-size-' + suffix);
  }


  //Link CSS and set up CSS variables
  const cssFile = document.getElementById('template_algolia_instant_search.css');
  document.documentElement.style.setProperty(`--main-color`, `${algolia.config.colors.main}`);
  document.documentElement.style.setProperty(`--secondary-color`, `${algolia.config.colors.secondary}`);
  document.documentElement.style.setProperty(`--highlight-bg-color`, `${algolia.helpers.hexToRGB(algolia.config.colors.highlight)}`);
  algolia.appendStyle(cssFile.text);

  if (collectionPageEnabled) {
    if (
      document.querySelectorAll(algolia.config.collection_css_selector)
        .length === 0
    ) {
      throw new Error(
        'Instant search CSS selector for collection page is incorrect\nFor more info see: https://www.algolia.com/doc/integration/shopify/advanced-customization/collection-search-page/#css-selector'
      );
    }
  } else if (
    document.querySelectorAll(algolia.config.results_selector).length === 0
  ) {
    throw new Error(
      'Instant search CSS selector is incorrect\nFor more info see: https://www.algolia.com/doc/integration/shopify/building-search-ui/instant-search/#css-selector'
    );
  }

  // Instantiating the main page
  instant.$results = document.querySelector(instant.selector);
  instant.$results.innerHTML = '';

  algolia.render(algolia.instantSearchMainTemplate, instant.$results, {
    facets: instant.facets.list,
    multipleSortOrders: typeof activeSortOrders != "undefined" && activeSortOrders.length > 1,
  });

  readjust();

  window.addEventListener('resize', function() {
    readjust();
  });

  // Mobile facets display
  instant.search.addWidgets([
    {
      init: function() {
        var $button = document.querySelector('.ais-facets-button');
        $button.addEventListener('click', function() {
          var $facets = document.querySelector('.ais-facets');

          if ($facets.classList.contains('ais-facets__shown')) {
            $button.textContent = 'Show filters';
            $facets.classList.remove('ais-facets__shown');
          } else {
            $button.textContent = 'Hide filters';
            $facets.classList.add('ais-facets__shown');
          }
        });
      },
    },
  ]);

  instant.search.addWidgets([
    configure({
      hitsPerPage: instant.hitsPerPage,
      facetingAfterDistinct: false,
    }),
  ]);

  // Search input
  instant.search.addWidgets([
    searchBox({
      container: '.ais-search-box-container',
      placeholder: algolia.translations.searchForProduct,
      showReset: false,
      showSubmit: false,
    }),
  ]);

  // Logo & clear
  instant.search.addWidgets([
    {
      init: function(opts) {
        document
          .querySelector('.ais-clear-input-icon')
          .addEventListener('click', function() {
            opts.helper.setQuery('').search();
            var input = document.querySelector('.ais-search-box--input');
            input.value = '';
            input.focus();
          });
      },
      render: function(opts) {
        if (!opts.state.query) {
          document.querySelector('.ais-clear-input-icon').style.display =
            'none';
        } else {
          document.querySelector('.ais-clear-input-icon').style.display = '';
        }
      },
    },
  ]);

  // Stats
  instant.search.addWidgets([
    stats({
      container: '.ais-stats-container',
      templates: {
        text(data, {html}){
          return algolia.instantSearchStatsTemplate(data, html)
        }
      }
    }),
  ]);

  // Sort orders
  if (activeSortOrders.length > 1) {
    instant.search.addWidgets([
      sortBy({
        container: '.ais-sort-orders-container',
        items: instant.sortOrders,
      }),
    ]);
  }

  // Change display
  instant.search.addWidgets([
    {
      init: function() {
        document
          .querySelector('.ais-search-header .ais-change-display-block')
          .addEventListener('click', function() {
            document
              .querySelector(
                '.ais-change-display-block:not(.ais-change-display-selected)'
              )
              .classList.add('ais-change-display-selected');
            document
              .querySelector(
                '.ais-change-display-list.ais-change-display-selected'
              )
              .classList.remove('ais-change-display-selected');
            document
              .querySelector('.ais-results-as-list')
              .classList.replace('ais-results-as-list', 'ais-results-as-block');
          });
        document
          .querySelector('.ais-search-header .ais-change-display-list')
          .addEventListener('click', function() {
            document
              .querySelector(
                '.ais-change-display-list:not(.ais-change-display-selected)'
              )
              .classList.add('ais-change-display-selected');
            document
              .querySelector(
                '.ais-change-display-block.ais-change-display-selected'
              )
              .classList.remove('ais-change-display-selected');
            document
              .querySelector('.ais-results-as-block')
              .classList.replace('ais-results-as-block', 'ais-results-as-list');
          });
      },
    },
  ]);

  // Hidden facets
  var list = instant.facets.hidden.map(function(facet) {
    return facet.name;
  });
  instant.search.addWidgets([
    {
      getConfiguration: function() {
        return {
          facets: list,
          disjunctiveFacets: list,
        };
      },
      init: function() {},
    },
  ]);

  // Create the render function
  var createDataAttributes = function(refinement) {
    return Object.keys(refinement)
      .map(function(key) {
        return 'data-' + key + '="' + encodeURIComponent(refinement[key] || '') + '"';
      })
      .join(' ');
  };

  var renderListItem = function(item) {
    var facet = instant.facets.list.find(function(f) {
      return f.name === item.label;
    });
    return item.refinements
      .map(function(refinement) {
        return (
          '<li class="ais-current-refined-values--item">' +
          '  <a ' +
          createDataAttributes(refinement) +
          '    class="ais-current-refined-values--link">' +
          '    <div>' +
          '      <div class="ais-current-refined-values--label">' +
          facet.title +
          '      </div>: ' +
          refinement.label +
          '    </div>' +
          '  </a>' +
          '</li>'
        );
      })
      .join('');
  };

  var renderCurrentRefinements = function(renderOptions) {
    var items = renderOptions.items;
    var refine = renderOptions.refine;
    var widgetParams = renderOptions.widgetParams;

    widgetParams.container.innerHTML =
      '<div class="ais-current-refined-values--header ais-facet--header ais-header">Selected filters</div>' +
      '<div class="ais-root ais-current-refined-values ais-facet">' +
      '  <ul class="ais-current-refined-values--list">' +
      items.map(renderListItem).join('') +
      '  </ul>' +
      '</div>';

    Array.prototype.slice
      .call(
        widgetParams.container.querySelectorAll(
          '.ais-current-refined-values--link'
        )
      )
      .forEach(function(element) {
        element.addEventListener('click', function(event) {
          var item = Object.keys(event.currentTarget.dataset).reduce(function(
            acc,
            key
          ) {
            var itemData = {};
            itemData[key] = decodeURIComponent(event.currentTarget.dataset[key]);
            return algolia.assign({}, acc, itemData);
          },
          {});

          refine(item);
        });
      });
  };

  // Create the custom widget
  var customCurrentRefinements = connectCurrentRefinements(
    renderCurrentRefinements
  );

  /**
   * Checks if any facets in the list have active refinements.
   * 
   * Examines conjunctive, disjunctive, hierarchical, and numeric facets
   * to determine if any have been refined by the user.
   * 
   * @function hasRefinements
   * @param {Object} helper - Algolia helper instance with state
   * @param {Array} facetsList - List of configured facet objects
   * @returns {boolean} True if any facet has refinements, false otherwise
   */
  var hasRefinements = function(helper, facetsList) {
    return facetsList.some(function(f) {
      var isConjunctiveFacetRefined =
        helper.state.isConjunctiveFacet(f.name) &&
        helper.state.isFacetRefined(f.name);
      var isDisjunctiveFacetRefined =
        helper.state.isDisjunctiveFacet(f.name) &&
        helper.state.isDisjunctiveFacetRefined(f.name);
      var isHierarchicalFacetRefined =
        helper.state.isHierarchicalFacet(f.name) &&
        helper.state.isHierarchicalFacetRefined(f.name);

      var numericRefinements = helper.state.getNumericRefinements(f.name);
      var isNumericFacetRefined = Object.keys(numericRefinements).some(function(
        operator
      ) {
        return numericRefinements[operator].length > 0;
      });

      return (
        isNumericFacetRefined ||
        isConjunctiveFacetRefined ||
        isDisjunctiveFacetRefined ||
        isHierarchicalFacetRefined
      );
    });
  };

  /**
   * Decides whether the given `facetName` has any facet values available in
   * the current result set by checking the available `facets`,
   * `disjunctiveFacets` and `hierarchicalFacets`.
   * 
   * Used to conditionally show/hide facet panels based on whether there
   * are any filterable values in the current search results.
   * 
   * @function hasFacetValues
   * @param {Object} resultState - Algolia results state containing facet data
   * @param {string} facetName - The name of the facet to check
   * @returns {boolean} True if facet has available values, false otherwise
   */
  var hasFacetValues = function(resultState, facetName) {
    var isFacetValueAvailable = resultState.facets.some(function(facet) {
      return facet.name === facetName;
    });
    var isDisjunctiveFacetValueAvailable = resultState.disjunctiveFacets.some(
      function(facet) {
        return facet.name === facetName;
      }
    );
    var isHierarchicalFacetValueAvailable = resultState.hierarchicalFacets.some(
      function(facet) {
        return facet.name === facetName;
      }
    );

    return (
      isFacetValueAvailable ||
      isDisjunctiveFacetValueAvailable ||
      isHierarchicalFacetValueAvailable
    );
  };

  var customCurrentRefinementsWithPanel = panel({
    hidden: function(options) {
      return !hasRefinements(options.helper, instant.facets.list);
    },
  })(customCurrentRefinements);

  var clearRefinementsWithPanel = panel({
    hidden: function(options) {
      return !hasRefinements(options.helper, instant.facets.list);
    },
  })(clearRefinements);

  instant.search.addWidgets([
    clearRefinementsWithPanel({
      container: document.querySelector('.ais-clear-refinements-container'),
      templates: {
        resetLabel: algolia.translations.clearAll,
      },
    }),
    customCurrentRefinementsWithPanel({
      container: document.querySelector(
        '.ais-current-refined-values-container'
      ),
    }),
  ]);

  /**
   * Setup the facets for search.
   * We are using a `panel` with a custom `hidden` function which will allow
   * us to hide the parent container of the facet if there are no facet values
   * available for that particular facet.
   */
  instant.facets.widgets.forEach(function(widget) {
    if(widget) {
      var facetWithPanel = panel({
        hidden: function(options) {
          var hide = !hasFacetValues(options.results, widget.params.attribute);
          var displayStyle = hide ? 'none' : '';

          /**
           * Replacing any "." and "%" in the class name with "\." and "\%" so that querySelector
           * can work as expected. "." is supposed to be present for named tags
           * and metafield based facet names and "%" is present when the facet name has a space
           * in it because of the encoding.
           */
          var parentContainerClassName = '.ais-facet-' + encodeURIComponent(
            widget.params.attribute
          ).replace(/\./g, '\\.').replace(/\%/g, '\\%');
          var parentContainer = document.querySelector(parentContainerClassName);

          if (parentContainer) {
            parentContainer.style.display = displayStyle;
          }

          return hide;
        },
      })(widget.widget);

      instant.search.addWidgets([facetWithPanel(widget.params)]);
    }
  });

  /**
   * Updates the search page title and results count display.
   * 
   * Called when search results change to update:
   * - Document title with result count and search query
   * - Results count in the stats container
   * 
   * This provides better UX by showing real-time result counts.
   * 
   * @function updateSearchPageNumberOfResults
   * @param {Array} items - Array of search result hit objects
   * @returns {void}
   * @sideEffect Modifies document.title and DOM element textContent
   */
  function updateSearchPageNumberOfResults(items) {
    let titleTarget = document.querySelector('title');
    let length = items.length;
    let resultsString = length > 1 ? 'results' : 'result';

    titleTarget.innerText = `Search: ${length} ${resultsString} found for "${instant?.search?._initialUiState[algolia_index_name]?.query}" – Robert's Medical Uniforms`;

    let resultsTarget = document.querySelector('.ais-stats-container .ais-stats--nb-results .number-of-results');
    if(resultsTarget) {
      resultsTarget.innerText = length;
    }
  }

  /**
   * Hits widget configuration with custom rendering and transformation.
   * 
   * Configures the product display including:
   * - Custom price processing via algolia_custom_helpers
   * - Swatch count display
   * - Product template rendering
   * - Item transformation for inventory/analytics data
   * 
   * @see {@link https://www.algolia.com/doc/ui-libraries/instantsearch/api-reference/instantsearch-widget/hits/}
   */
  // Hits
  instant.search.addWidgets([
    hits({
      container: '.ais-hits-container',
      templates: {
        /**
         * Renders individual product hit in search results.
         * 
         * @param {Object} hit - Algolia hit object with product data
         * @param {Object} params - Template parameters including html and components
         * @returns {string} HTML string for the product card
         */
        item(hit, {html, components}) {
          let algoliaCustomHelpers = new algolia_custom_helpers();
          // Get custom prices from product metafields and append them to results
          algoliaCustomHelpers.getProductMetafieldsAndProcessPrices(hit, instant.hitsPerPage);

          /**
           * Async update of swatch count after DOM render.
           * setTimeout ensures the card element exists before we try to modify it.
           */
          setTimeout(() => {
            algoliaCustomHelpers.addSwatchesCountToCard(hit);
          },1);

          return algolia.instantSearchProductTemplate(hit, html, components);
        },
        /**
         * Renders empty state when no results match the search.
         * 
         * @param {Object} results - Algolia results object
         * @param {Object} params - Template parameters
         * @returns {string} HTML string for no results message
         */
        empty(results, {html}) {
          return algolia.instantSearchNoResultTemplate(html);
        }
      },
      /**
       * Transforms search results before rendering.
       * 
       * Adds computed properties to each hit:
       * - _distinct: Whether distinct is enabled
       * - can_order: Whether product can be ordered (based on inventory)
       * - queryID: Required for Algolia analytics
       * - productPosition: Position in search results
       * - index: Name of the search index
       * 
       * Also updates page title on search page with result count.
       * 
       * @param {Array} items - Array of Algolia hit objects
       * @returns {Array} Transformed items array
       */
      transformItems: function(items) {
        if(document.querySelector('body').classList.contains('template-search')) {
          updateSearchPageNumberOfResults(items);
        }

        return items.map( item => ({
          ...item,
          _distinct: instant.distinct,
          can_order:
            item.inventory_management !== 'shopify' ||
            item.inventory_policy === 'continue' ||
            item.inventory_quantity > 0,
          queryID: item.__queryID,
          productPosition: item.__position,
          index: instant.search.mainIndex.getIndexName()
        }));
      }
    })
  ]);

  // Redirect to product on click
  instant.search.addWidgets([
    {
      init: function() {
        var container = document.querySelector('.ais-hits-container');
        container.addEventListener('click', function(e) {
          var el = e.target;
          var hit;
          do {
            if (el.matches('.ais-hit')) {
              hit = el;
              break;
            }
            el = el.parentNode;
          } while (el && el !== container);
          if (!hit) {
            return;
          }
          var $this = hit;
          var productHandle = $this.dataset.handle;
          var variant_id = $this.dataset.variantId;
          var distinct = $this.dataset.distinct;

          // Construct the link we should redirect to
          var link = '';

          // If we're on a collection page and the handle is available
          // then prefix the product link with the collection handle
          if (collectionPageEnabled && collectionHandle) {
            link = `${window.Shopify.routes.root}collections/${collectionHandle}`;
          }

          // Add the product handle to the link
          link = link + `${window.Shopify.routes.root}products/${productHandle}`;

          // Add the variant ID details to redirect to a specific
          // variant ID if we are showing variants and not products
          if (distinct !== 'true') {
            link += '?variant=' + variant_id;
          }

          if (algolia.config.analytics_enabled) {

            const clickData = {
              index: hit.dataset.algoliaIndex,
              eventName: 'Add To Cart',
              queryID: hit.dataset.algoliaQueryid,
              objectIDs: [hit.dataset.algoliaObjectid],
            };

            algolia.saveForConversionTracking(clickData);
          }
          window.location.href = link;
        });
      },
    },
  ]);

  // Add to cart
  instant.search.addWidgets([
    {
      init: function() {
        var container = document.querySelector('.ais-hits-container');
        container.addEventListener('click', function(e) {
          var el = e.target;
          var cartButton;
          do {
            if (el.matches('.ais-hit--cart-button')) {
              cartButton = el;
              break;
            }
            el = el.parentNode;
          } while (el && el !== container);
          if (!cartButton) {
            return;
          }

          e.stopPropagation();
          var $this = cartButton;
          var formId = $this.dataset.formId;

          if (formId) {
            document.getElementById(formId).submit();
          }
        });
      },
    },
  ]);

  // No result actions
  instant.search.addWidgets([
    {
      init: function(opts) {
        var container = document.querySelector('.ais-hits-container');
        container.addEventListener('click', function(e) {
          var el = e.target;
          var clearFilters;
          do {
            if (el.matches('.ais-hit-empty--clear-filters')) {
              clearFilters = el;
              break;
            }
            el = el.parentNode;
          } while (el && el !== container);
          if (!clearFilters) {
            return;
          }

          var helper = opts.helper;
          helper.clearTags();
          instant.facets.list.forEach(function(facet) {
            helper.clearRefinements(facet.name);
          });
          helper.search();
        });

        container.addEventListener('click', function(e) {
          var el = e.target;
          var clearInput;
          do {
            if (el.matches('.ais-hit-empty--clear-input')) {
              clearInput = el;
              break;
            }
            el = el.parentNode;
          } while (el && el !== container);
          if (!clearInput) {
            return;
          }

          opts.helper.setQuery('').search();
          var input = document.querySelector('.ais-search-box--input');
          input.value = '';
          input.focus();
        });
      },
    },
  ]);

  // Pagination
  instant.search.addWidgets([
    pagination({
      container: '.ais-pagination-container',
      padding: 2,
      maxPages: 20,
    }),
  ]);

  instant.search.start();

  algolia.contentHide.parentNode.removeChild(algolia.contentHide);
})(window.algoliaShopify);
